<template>
    <div v-if="isLoading">
        <span class="fa fa-spinner fa-spin"></span>
    </div>
    <section v-else>
        <error-messages :error="hasError"
                        :error-unauthenticated="pleaseLoginAgain"
                        :error-empty="!dispatchRequests.length"
                        :error-not-found="salesOrderNotFound"
                        :is-order-from-today="isOrderFromToday"
        />

        <alert success v-if="isSaveSuccessful === true" @close="closeAlert">
            De gevraagde leveringsverzoeken zijn geannuleerd.
        </alert>

        <alert v-if="isSaveSuccessful === false" @close="closeAlert">
            Er is iets mis gegaan bij het aanvragen van de annuleringen.
        </alert>

        <b-card v-for="dispatchRequest in dispatchRequests" :key="dispatchRequest.id" class="mb-4">
            <b-card-title>
                <h5>
                    <dispatch-request-status :status="dispatchRequest.status"/>
                    (via
                    <dispatcher-type :type="dispatchRequest.dispatcher_type"
                                     :name="dispatchRequest.dispatcher_identifier"/>
                    ,
                    leveringsverzoek {{ dispatchRequest.id }})
                </h5>

                <table class="generic">
                    <tr>
                        <th style="width:150px;"><small><strong>Laatste update:</strong></small></th>
                        <td><small>{{ dispatchRequest.updated_at | datetime }}</small></td>
                    </tr>
                    <tr>
                        <th><small><strong>Leverdatum:</strong></small></th>
                        <td><small>{{ dispatchRequest.dispatch_date | date }}</small></td>
                    </tr>
                    <tr>
                        <th><small><strong>Type:</strong></small></th>
                        <td><small>{{ dispatchRequest.dispatch_type }}</small></td>
                    </tr>
                    <tr>
                        <th><small><strong>Vervoerder:</strong></small></th>
                        <td><small>{{ dispatchRequest.carrier.display_name }}</small></td>
                    </tr>
                    <tr>
                        <th><small><strong>Vervoersmethode:</strong></small></th>
                        <td><small>{{ dispatchRequest.carrier_method.display_name }}</small></td>
                    </tr>
                </table>
            </b-card-title>

            <b-table-simple class="generic">
                <colgroup>
                    <col width="10%">
                    <col width="auto">
                    <col width="10%">
                    <col width="10%">
                </colgroup>
                <thead>
                <tr>
                    <th>Artikel nr.</th>
                    <th>Productnaam</th>
                    <th>Aantal</th>
                    <th>Annuleren</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="product in dispatchRequest.lines" :key="product.product_identifier">
                    <td>{{ product.product_identifier }}</td>
                    <td>{{ product.product_name }}</td>
                    <td>{{ product.quantity }}</td>
                    <td>
                        <input v-if="dispatchRequest.is_cancellable"
                               type="number"
                               min="0"
                               :max="product.quantity"
                               :disabled="isSaving"
                               v-model="product.quantity_to_cancel"
                        />
                        <small v-else-if="dispatchRequest.status === 'cancelled'">Al geannuleerd</small>
                        <small v-else>Niet mogelijk</small>
                    </td>
                </tr>
                </tbody>
            </b-table-simple>
        </b-card>
        <div v-if="dispatchRequests.length" class="clearfix">
            <button class="button"
                    @click="submitCancellation"
                    :disabled="isSubmitDisabled">
                <span v-if="!isSaving">Annuleringen opslaan</span>
                <span v-else class="fa fa-spinner fa-spin"></span>
            </button>
        </div>
    </section>
</template>

<script>
import DispatchRequestStatus from "../../components/DispatchRequestStatus"
import DispatcherType from "../../components/DispatcherType"
import ErrorMessages from "../../components/ErrorMessages"
import Alert from "../../components/Alert"

export default {
    components: {
        DispatcherType,
        DispatchRequestStatus,
        ErrorMessages,
        Alert,
    },

    props: {
        salesOrder: {required: true}
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            hasError: false,
            salesOrderNotFound: false,
            pleaseLoginAgain: false,
            isSaveSuccessful: null,
            dispatchRequests: [],
        }
    },

    computed: {
        isSubmitDisabled() {
            return this.isLoading || this.isSaving || this.postData.length <= 0 || this.hasInvalidQuantities
        },

        hasInvalidQuantities() {
            return this.dispatchRequests
                .map(dispatchRequest => {
                    return !!dispatchRequest.lines
                        .map(line => line.quantity_to_cancel && line.quantity_to_cancel > line.quantity)
                        .filter(bool => bool)
                        .length > 0
                })
                .filter(bool => bool)
                .length > 0
        },

        postData() {
            return this.dispatchRequests.map(dispatchRequest => {
                return {
                    id: dispatchRequest.id,
                    cancel: dispatchRequest.lines.map(line => {
                        if (line.quantity_to_cancel <= 0) return null
                        return {
                            product_identifier: line.product_identifier,
                            quantity: line.quantity_to_cancel
                        }
                    }).filter(line => line)
                }
            }).filter(dispatchRequest => dispatchRequest.cancel.length > 0)
        },
        isOrderFromToday() {
            // We get the order date from the identifier to avoid having to get the data from the OrderSuite DB
            let salesOrderDate = this.salesOrder.identifier.split('-')[0]
            if (parseInt(salesOrderDate.substr(-2)) !== new Date().getDate()) return false
            if (parseInt(salesOrderDate.substr(2, 2)) !== new Date().getMonth() + 1) return false
            if (parseInt(salesOrderDate.substr(0, 2)) !== new Date().getFullYear() - 2000) return false

            return true
        }
    },

    methods: {
        submitCancellation() {
            // Post the cancelation request
            this.isSaving = true
            this.isSaveSuccessful = null

            const body = {
                request: this.postData,
                timestamp: new Date().toISOString(),
            }

            this.$http.post('/api/omt/sales-order-cancellations/' + this.salesOrder.identifier, body)
                .then(response => {
                    this.dispatchRequests = this.addQuantityToCancel(response.data.response)
                    this.isSaveSuccessful = true
                })
                .catch(() => this.isSaveSuccessful = false)
                .finally(() => this.isSaving = false)
        },

        addQuantityToCancel(dispatchRequests) {
            return dispatchRequests.map(dispatchRequest => {
                dispatchRequest.lines = dispatchRequest.lines.map(line => {
                    return {
                        quantity_to_cancel: 0,
                        ...line
                    }
                })
                return dispatchRequest
            })
        },

        closeAlert() {
            this.isSaveSuccessful = null
        },
    },

    mounted() {
        this.$http.get('/api/omt/sales-order-cancellations/' + this.salesOrder.identifier)
            .then(response => this.dispatchRequests = this.addQuantityToCancel(response.data.response))
            .catch(error => {
                this.hasError = true
                if (error.response.status === 401) this.pleaseLoginAgain = true
                if (error.response.data.errors[0].identifier === 'SalesOrderNotFound') {
                    this.$connections.dismissErrors()
                    this.salesOrderNotFound = true
                }
            })
            .then(() => this.isLoading = false)
    }
}
</script>

<style scoped>
.clearfix {
    clear: both;
    overflow: auto;
}

h3 {
    margin: .5em 0;
}

table.generic {
    margin-bottom: 20px;
}

input[type=number] {
    font-size: 12px;
    padding: 2px 3px;
    width: 34px;
}

button {
    float: right;
}

button[disabled] {
    color: #999;
    border-color: #aaa;
}
</style>
